/**
 * Metrics Management Component
 *
 * Main container component for workspace metrics dashboard.
 * Manages workspace selection, period filtering, and displays all metric visualizations.
 */

import React, { useState, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { LoadingSpinner } from '../../../../common';
import { AdminPageHeader, AdminEmptyState, WorkspaceFilter } from '../../../ui';
import { useWorkspacesWithAnalytics } from '../hooks/useWorkspacesWithAnalytics';
import { useWorkspaceMetrics } from '../hooks/useWorkspaceMetrics';
import { MetricsSummaryCards } from './MetricsSummaryCards';
import { TokenUsageChart } from './TokenUsageChart';
import { UserActivityChart } from './UserActivityChart';
import { AgentUsageChart } from './AgentUsageChart';
import { WorkspaceHealthCard } from './WorkspaceHealthCard';
import { MetricsPeriod, METRICS_PERIOD_LABELS } from '../../../../../types/metrics';

export const MetricsManagement: React.FC = () => {
  const { workspaces, isLoading: isLoadingWorkspaces } = useWorkspacesWithAnalytics();
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<number | null | 'global'>(null);

  // Convert 'global' to null for metrics hook (metrics are workspace-specific)
  const workspaceIdForMetrics = selectedWorkspaceId === 'global' ? null : selectedWorkspaceId;

  const { analytics, tokenHistory, isLoading, error, period, setPeriod, refetch } =
    useWorkspaceMetrics(workspaceIdForMetrics);

  const handleWorkspaceChange = useCallback((workspaceId: number | null | 'global') => {
    setSelectedWorkspaceId(workspaceId);
  }, []);

  const handlePeriodChange = useCallback(
    (newPeriod: MetricsPeriod) => {
      setPeriod(newPeriod);
    },
    [setPeriod]
  );

  const handleRefresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return (
    <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6">
      <AdminPageHeader
        title="Métricas do Workspace"
        subtitle="Análise detalhada de uso e performance"
        actions={
          selectedWorkspaceId && (
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="
                flex items-center gap-2 px-4 py-2
                bg-[#333333] hover:bg-[#444444]
                text-white font-medium
                rounded-lg transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed
              "
            >
              <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
              Atualizar
            </button>
          )
        }
      />

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Workspace and Period Selectors */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1">
          <WorkspaceFilter
            workspaces={workspaces}
            selectedWorkspaceId={selectedWorkspaceId}
            onWorkspaceChange={handleWorkspaceChange}
            isLoading={isLoadingWorkspaces}
          />
        </div>

        {selectedWorkspaceId && (
          <div className="flex gap-2">
            {([7, 30, 90] as MetricsPeriod[]).map((p) => (
              <button
                key={p}
                onClick={() => handlePeriodChange(p)}
                className={`
                  px-4 py-2 text-sm font-medium rounded-lg transition-colors
                  ${
                    period === p
                      ? 'bg-[#F4CC04] text-black'
                      : 'bg-[#333333] text-[#cccccc] hover:bg-[#444444]'
                  }
                `}
              >
                {METRICS_PERIOD_LABELS[p]}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      {!selectedWorkspaceId ? (
        <AdminEmptyState
          title="Selecione um Workspace"
          description="Escolha um workspace no filtro acima para visualizar as métricas"
        />
      ) : isLoading && !analytics ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner size="large" />
        </div>
      ) : analytics ? (
        <>
          {/* Summary Cards */}
          <MetricsSummaryCards analytics={analytics} />

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Left Column */}
            <div className="space-y-6">
              <TokenUsageChart data={tokenHistory} />
              <UserActivityChart data={analytics.user_activity} />
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              <AgentUsageChart data={analytics.agent_usage} />
              <WorkspaceHealthCard analytics={analytics} />
            </div>
          </div>

          {/* Footer Info */}
          <div className="text-center text-sm text-[#666666]">
            Dados do período: {new Date(analytics.period_start).toLocaleDateString('pt-BR')} até{' '}
            {new Date(analytics.period_end).toLocaleDateString('pt-BR')}
          </div>
        </>
      ) : null}
    </div>
  );
};
